import { getBusinessObject, is } from 'bpmn-js/lib/util/ModelUtil'
import { getDocumentationTemplate, getDocumentationMarkdown, getRoles } from './util'

export default function SDocumentation(eventBus, overlays, bpmnjs, elementRegistry) {
  function createOrUpdateOverlay(element, { formRef, documentationMarkdown, raciRoles }) {
    let overlayEntry = overlays.get({ element, type: 'sistemiv' })[0]
    let overlay

    if (overlayEntry) {
      overlay = overlayEntry.html
    } else {
      overlay = document.createElement('div')
      overlay.className = 'flex flex-row space-x-0.5'

      if (is(element, 'bpmn:Gateway')) {
        overlays.add(element, 'sistemiv', {
          position: {
            bottom: 70,
            right: 20,
          },
          html: overlay,
        })
      } else if (is(element, 'bpmn:Event')) {
        overlays.add(element, 'sistemiv', {
          position: {
            bottom: 60,
            right: 20,
          },
          html: overlay,
        })
      } else {
        overlays.add(element, 'sistemiv', {
          position: {
            bottom: 100,
            right: 50,
          },
          html: overlay,
        })
      }
    }

    if (documentationMarkdown && !overlay.querySelector('.sistemiv-overlay-documentation')) {
      const docButton = document.createElement('div')
      docButton.className = 'sistemiv-overlay-documentation'
      docButton.innerHTML = `
                            <div
                              class="group inline-flex items-center w-7 h-7 hover:w-24 overflow-hidden bg-blue-500 text-white rounded-full cursor-pointer transition-all duration-300" title="Documentation"
                            >
                              <div class="w-7 h-7 flex items-center justify-center shrink-0">
                                <img
                                  src="https://cdn-icons-png.flaticon.com/128/3914/3914955.png"
                                  alt="Docs"
                                  class="w-[18px] h-[18px] filter invert"
                                />
                              </div>
                              <span class="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap text-sm font-medium">
                                Docs
                              </span>
                            </div>
                          `

      docButton.addEventListener('click', () => {
        eventBus.fire('documentation.open', {
          element,
          markdown: documentationMarkdown,
        })
      })

      overlay.appendChild(docButton)
    }

    if (formRef && !overlay.querySelector('.sistemiv-overlay-form')) {
      const formButton = document.createElement('div')
      formButton.className = 'sistemiv-overlay-form'
      formButton.innerHTML = `
                            <div
                              class="group inline-flex items-center w-7 h-7 hover:w-24 overflow-hidden bg-green-500 text-white rounded-full cursor-pointer transition-all duration-300" title="Form preview"
                            >
                              <div class="w-7 h-7 flex items-center justify-center shrink-0">
                                <img
                                  src="https://cdn-icons-png.flaticon.com/128/7857/7857546.png"
                                  alt="Form"
                                  class="w-[18px] h-[18px] filter invert"
                                />
                              </div>
                              <span class="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap text-sm font-medium">
                                Form
                              </span>
                            </div>
                          `

      formButton.addEventListener('click', () => {
        eventBus.fire('form.open', {
          element,
          formRef,
        })
      })
      overlay.appendChild(formButton)
    }
    if (Array.isArray(raciRoles) && raciRoles.length > 0 && !overlay.querySelector('.sistemiv-overlay-raci')) {
      const raciButton = document.createElement('div')
      raciButton.className = 'sistemiv-overlay-raci'
      raciButton.innerHTML = `
                            <div
                              class="group inline-flex items-center w-7 h-7 hover:w-24 overflow-hidden bg-orange-400 text-white rounded-full cursor-pointer transition-all duration-300" title ="RACI matrix"
                            >
                              <div class="w-7 h-7 flex items-center justify-center shrink-0">
                                <img
                                  src="https://cdn-icons-png.flaticon.com/512/10742/10742236.png"
                                  alt="Raci"
                                  class="w-[18px] h-[18px] filter invert"
                                />
                              </div>
                              <span class="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap text-sm font-medium">
                                RACI
                              </span>
                            </div>
                          `

      const name = getBusinessObject(element)?.name || getBusinessObject(element)?.id || element.id
      raciButton.addEventListener('click', () => {
        eventBus.fire('raci.open', {
          element,
          raci: [{ element: name, roles: raciRoles }],
        })
      })
      overlay.appendChild(raciButton)
    }
  }

  function ensureGlobalButtonContainer() {
    const container = document.getElementById('bpmn-viewer')
    if (!container) return null

    let wrapper = container.querySelector('#global-buttons-wrapper')
    if (!wrapper) {
      wrapper = document.createElement('div')
      wrapper.id = 'global-buttons-wrapper'
      wrapper.className = 'absolute bottom-4 left-16 z-50 flex flex-col gap-2'
      container.appendChild(wrapper)
    }

    return wrapper
  }

  function addGlobalProcessDocsButton(documentationMarkdown) {
    const wrapper = ensureGlobalButtonContainer()
    if (!wrapper) return

    const existingBtn = wrapper.querySelector('#global-process-docs-btn')
    if (existingBtn) existingBtn.remove()

    const button = document.createElement('div')
    button.id = 'global-process-docs-btn'
    button.className = 'cursor-pointer bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition'
    button.innerText = 'Process Docs'

    button.addEventListener('click', () => {
      eventBus.fire('documentation.open', {
        element: null,
        markdown: documentationMarkdown,
      })
    })

    wrapper.appendChild(button)
  }

  function addGlobalRaciButton(shapes) {
    const raci = shapes
      .map((shape) => {
        const roles = getRoles(shape)
        const element = getBusinessObject(shape)
        const name = element?.name || element?.id || shape.id
        return roles ? { element: name, roles } : null
      })
      .filter(Boolean)

    if (!raci.length) return

    const wrapper = ensureGlobalButtonContainer()
    if (!wrapper) return

    const existingBtn = wrapper.querySelector('#global-raci-btn')
    if (existingBtn) existingBtn.remove()

    const button = document.createElement('div')
    button.id = 'global-raci-btn'
    button.className = 'cursor-pointer bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition'
    button.innerText = 'RACI Matrix'

    button.addEventListener('click', () => {
      eventBus.fire('raci.open', {
        element: null,
        raci,
      })
    })

    wrapper.appendChild(button)
  }

  eventBus.on('shape.added', function (event) {
    const element = event.element
    if (element.labelTarget) return
    const businessObject = getBusinessObject(element)

    const documentationMarkdown = getDocumentationTemplate(element) && getDocumentationMarkdown(element)
    const formRef = is(element, 'bpmn:UserTask') && businessObject.get('camunda:formRef')
    const raciRoles = getRoles(element)

    if (documentationMarkdown || formRef || raciRoles) {
      defer(() =>
        createOrUpdateOverlay(element, {
          formRef,
          documentationMarkdown,
          raciRoles,
        }),
      )
    }
  })

  eventBus.on('import.done', function () {
    const canvas = bpmnjs.get('canvas')
    const rootElement = canvas.getRootElement() // bpmn:Process

    const elements = elementRegistry.getAll()
    console.log('All BPMN Elements:', elements)

    const documentationMarkdown = getDocumentationTemplate(rootElement) && getDocumentationMarkdown(rootElement)

    if (documentationMarkdown) {
      defer(() => {
        addGlobalProcessDocsButton(documentationMarkdown)
      })
    }
    defer(() => {
      addGlobalRaciButton(elements)
    })
  })
}

SDocumentation.$inject = ['eventBus', 'overlays', 'bpmnjs', 'elementRegistry']

// helpers

function defer(fn) {
  setTimeout(fn, 0)
}
